import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCachedResource } from "../hooks/useCachedResource";
import { PurchaseShell } from "../features/purchase/PurchaseShell";
import { StatCard } from "../components/data/StatCard";
import { StatusChip } from "../components/data/StatusChip";
import { NeuCard } from "../components/neu/Neu";
import { purchaseOverview, listPurchaseDocs } from "../api/purchases";
import { fmtMoney } from "../lib/format";
import { localName } from "../lib/localName";

const STATS = [
  ["open_orders", "orders", "sky"],
  ["pending_orders", "docs", "amber"],
  ["pending_requisitions", "docs", "violet"],
  ["unpaid_bills", "due", "rose"],
  ["disputed_bills", "alert", "fuchsia"],
];
const AMOUNTS = [["open_orders_amount", "money", "emerald"], ["unpaid_amount", "due", "orange"]];

export default function PurchaseDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: dash } = useCachedResource("purchase-dashboard", async () => {
    const [ov, docs] = await Promise.all([
      purchaseOverview().catch(() => null),
      listPurchaseDocs({ doc_type: "purchase_order", limit: 6 }).catch(() => ({ items: [] })),
    ]);
    return { data: ov, latest: docs.items || [] };
  });
  const data = dash?.data || null;
  const latest = dash?.latest || [];

  return (
    <PurchaseShell titleKey="dashboard">
      {!data ? null : (
        <>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="purchase-kpis">
            {STATS.map(([key, icon, tone]) => (
              <StatCard key={key} testid={`purchase-kpi-${key}`} icon={icon} tone={tone}
                        label={t(`purchases.kpi.${key}`)} value={data.totals[key]} />
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="purchase-amounts">
            {AMOUNTS.map(([key, icon, tone]) => (
              <StatCard key={key} testid={`purchase-amount-${key}`} icon={icon} tone={tone}
                        label={t(`purchases.kpi.${key}`)} value={data.totals[key]} />
            ))}
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <NeuCard className="grid gap-3 p-4" data-testid="purchase-latest-orders">
              <p className="label-xs">{t("purchases.tabs.orders")}</p>
              {!latest.length ? (
                <p className="text-sm text-muted-foreground">{t("purchases.noDocs")}</p>
              ) : latest.map((doc) => (
                <button key={doc.id} type="button" data-testid={`latest-order-${doc.id}`}
                        onClick={() => navigate(`/purchases/docs/${doc.id}`)}
                        className="flex items-center gap-3 rounded-2xl p-3 text-start shadow-neu-in">
                  <span className="num font-mono text-[11px] text-primary">{doc.number || "—"}</span>
                  <span className="min-w-0 flex-1 truncate text-xs font-bold">
                    {localName(doc.supplier) || t("purchases.internal")}
                  </span>
                  <span className="num font-mono text-xs">
                    {doc.money?.currency} {fmtMoney(doc.totals?.payable)}
                  </span>
                  <StatusChip state={doc.state} />
                </button>
              ))}
            </NeuCard>

            <NeuCard className="grid gap-3 p-4" data-testid="purchase-top-suppliers">
              <p className="label-xs">{t("purchases.kpi.topSuppliers")}</p>
              {data.top_suppliers.map((row, index) => (
                <div key={row.supplier_id || row.name}
                     className="flex items-center gap-3 rounded-2xl p-3 shadow-neu-in">
                  <span className="num grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs font-bold">{row.name}</span>
                  <span className="num font-mono text-[11px] text-muted-foreground">{row.bills}</span>
                  <span className="num font-mono text-xs font-bold text-primary">{fmtMoney(row.amount)}</span>
                </div>
              ))}
            </NeuCard>
          </div>
        </>
      )}
    </PurchaseShell>
  );
}
